'use client';

import { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Reveal, { StaggerChildren, StaggerItem } from './Reveal';

const faqs = [
  {
    fr: {
      q: 'Quels types d\u2019entreprises accompagnez-vous ?',
      a: 'Nous travaillons principalement avec des PME et TPE de 5 à 30 employés, des associations et des organisations internationales qui n\u2019ont pas d\u2019équipe IT interne dédiée.',
    },
    en: {
      q: 'What types of businesses do you work with?',
      a: 'We primarily work with SMEs and small businesses of 5 to 30 employees, associations, and international organizations that don\u2019t have a dedicated internal IT team.',
    },
  },
  {
    fr: {
      q: 'Quelle est la différence entre Quorova et un prestataire IT classique ?',
      a: 'Contrairement aux MSP traditionnels, nous couvrons infrastructure IT, présence digitale et intégration IA dans une seule offre. Réseau, sécurité, cloud, mais aussi automatisation des workflows, outils connectés et solutions d\u2019IA adaptées à votre métier. Un seul partenaire pour tout votre numérique.',
    },
    en: {
      q: 'What\u2019s the difference between Quorova and a traditional IT provider?',
      a: 'Unlike traditional MSPs, we combine IT infrastructure, digital presence, and AI integration in a single offering. Network, security, cloud, but also workflow automation, connected tools, and AI solutions tailored to your business. One partner for your entire digital world.',
    },
  },
  {
    fr: {
      q: 'Comment fonctionne votre supervision 24/7 ?',
      a: 'Nous déployons des agents de monitoring sur vos équipements critiques. Les alertes sont traitées en temps réel et nous intervenons proactivement avant que les problèmes n\u2019impactent votre activité.',
    },
    en: {
      q: 'How does your 24/7 monitoring work?',
      a: 'We deploy monitoring agents on your critical equipment. Alerts are handled in real time and we intervene proactively before issues impact your operations.',
    },
  },
  {
    fr: {
      q: 'Proposez-vous des contrats sans engagement ?',
      a: 'Nous proposons des forfaits mensuels clairs et transparents. Après une période initiale de mise en place, nos contrats sont flexibles et sans engagement longue durée.',
    },
    en: {
      q: 'Do you offer contracts without long-term commitment?',
      a: 'We offer clear, transparent monthly plans. After an initial setup period, our contracts are flexible with no long-term commitment required.',
    },
  },
  {
    fr: {
      q: 'Combien de temps prend la mise en place ?',
      a: 'L\u2019audit initial prend généralement 1 à 2 semaines. Le déploiement complet de l\u2019infrastructure et des outils se fait progressivement sur 4 à 8 semaines selon la complexité de votre environnement.',
    },
    en: {
      q: 'How long does the setup take?',
      a: 'The initial audit typically takes 1 to 2 weeks. Full deployment of infrastructure and tools is done progressively over 4 to 8 weeks depending on the complexity of your environment.',
    },
  },
  {
    fr: {
      q: 'Travaillez-vous en français et en anglais ?',
      a: 'Oui, notre équipe est entièrement bilingue. Nous communiquons et documentons dans la langue de votre choix, ce qui est idéal pour les équipes internationales.',
    },
    en: {
      q: 'Do you work in both French and English?',
      a: 'Yes, our team is fully bilingual. We communicate and document in your preferred language, making us ideal for international teams.',
    },
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden bg-white/[0.02] hover:bg-white/[0.04] transition-colors duration-500">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-medium text-white">{q}</span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="shrink-0"
        >
          <ChevronDown className="w-5 h-5 text-white/30" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="px-6 pb-5 text-white/55 leading-relaxed">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const { lang, t } = useLanguage();

  return (
    <section id="faq" className="py-20 md:py-28 bg-dark-900">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-electric tracking-widest uppercase mb-3">
              FAQ
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t('Questions fréquentes', 'Frequently asked questions')}
            </h2>
            <p className="mt-4 text-lg text-white/50">
              {t(
                'Tout ce que vous devez savoir avant de nous contacter.',
                'Everything you need to know before reaching out.'
              )}
            </p>
          </div>
        </Reveal>

        <StaggerChildren className="space-y-3" stagger={0.08}>
          {faqs.map((faq, i) => {
            const content = lang === 'fr' ? faq.fr : faq.en;
            return (
              <StaggerItem key={i}>
                <FAQItem q={content.q} a={content.a} />
              </StaggerItem>
            );
          })}
        </StaggerChildren>
      </div>
    </section>
  );
}
